import {
  SectionId,
  UIInstrumentType,
} from '../../../components/DataFeed/Configuration/types';
import { SECTION_CONFIGS } from '../../../components/DataFeed/Configuration/configs/sectionConfigs';

/**
 * Sections that are optional and should not trigger warnings when empty
 */
const OPTIONAL_SECTIONS: SectionId[] = [
  SectionId.NOTE,
  SectionId.OTHER_CHARGES,
  SectionId.ACCRUED_INTEREST,
  SectionId.SETTLEMENT_ACCOUNT,
  SectionId.EX_DIVIDEND_DATE,
  SectionId.AWARDED_SHARES,
  SectionId.AWARDED_CASH,
];

/**
 * Get the list of mandatory section IDs for a given instrument type
 *
 * @param uiType - The UI instrument type
 * @returns Array of mandatory SectionIds for this instrument type
 */
export const getMandatorySectionsForInstrumentType = (
  uiType: UIInstrumentType
): SectionId[] => {
  // Get all sections visible for this instrument type
  const visibleSections = SECTION_CONFIGS.filter(section =>
    section.visibleInInstrumentTypes.includes(uiType)
  );

  // Filter to only mandatory sections (excluding optional ones and INSTRUMENT_IDENTIFIERS)
  const mandatorySections = visibleSections
    .filter(
      section =>
        !OPTIONAL_SECTIONS.includes(section.id) &&
        section.id !== SectionId.INSTRUMENT_IDENTIFIERS
    )
    .map(section => section.id);

  return mandatorySections;
};

/**
 * Check if a mandatory field is empty
 *
 * @param sectionId - The section identifier
 * @param value - The field value to check
 * @param uiType - The UI instrument type for the row (nullable)
 * @returns true if the field is mandatory AND empty, false otherwise
 */
export const isMandatoryFieldEmpty = (
  sectionId: string,
  value: any,
  uiType: UIInstrumentType | null | undefined
): boolean => {
  // If no instrument type, we can't determine mandatory status
  if (!uiType) {
    return false;
  }

  // Get mandatory sections for this instrument type
  const mandatorySections =
    getMandatorySectionsForInstrumentType(uiType);

  // Check if this section is mandatory
  const isMandatory = mandatorySections.includes(sectionId as SectionId);

  // Check if value is empty
  const isEmpty = value === null || value === undefined || value === '';

  // Return true only if field is mandatory AND empty
  return isMandatory && isEmpty;
};

/**
 * Check if an award field (AWARDED_SHARES / AWARDED_CASH) should show a warning.
 * For Dividend rows, at least one of the two must be provided.
 *
 * @returns true when both award fields are empty on a Dividend row
 */
export const isAwardFieldMissing = (
  sectionId: string,
  uiType: UIInstrumentType | null | undefined,
  row: Record<string, unknown>
): boolean => {
  if (
    sectionId !== SectionId.AWARDED_SHARES &&
    sectionId !== SectionId.AWARDED_CASH
  ) {
    return false;
  }

  if (!uiType) return false;

  if (uiType !== 'Dividend') return false;

  const sharesEmpty =
    row.AWARDED_SHARES === null ||
    row.AWARDED_SHARES === undefined ||
    row.AWARDED_SHARES === '';
  const cashEmpty =
    row.AWARDED_CASH === null ||
    row.AWARDED_CASH === undefined ||
    row.AWARDED_CASH === '';

  return sharesEmpty && cashEmpty;
};
